import { useFormikContext, useField } from 'formik';
import { InputHTMLAttributes } from 'react';
import { InputGroupProps } from '@blueprintjs/core';
import { handleMaxDecimalPlaces } from 'app/helpers';

import { FormGroup } from '../form/Form';
import { StyledInputGroup } from '../form/input/Input';
import { FieldError } from './field-error';

interface IFormikInputControlProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  /**
   * true = hide both validation styling and any messages
   *
   * "message" = only hide the message, but show validation styling
   */
  hideValidationErrors?: boolean | 'message';
  maxDecimalPlaces?: number;
}

export type FormikInputControlProps = IFormikInputControlProps & Omit<InputGroupProps, 'id' | 'value' | 'onChange'>;

export const FormikInputControl = (props: FormikInputControlProps) => {
  const context = useFormikContext<Record<string, any>>();
  const { name, label, required, helperText, hideValidationErrors, className, maxDecimalPlaces, ...inputGroupProps } =
    props;
  const fieldError = context.errors[name];
  const [field, meta, helper] = useField(name);
  const showErrorStyle = (!hideValidationErrors || hideValidationErrors === 'message') && meta.touched && !!fieldError;
  const showErrorMessage =
    (!hideValidationErrors || hideValidationErrors !== 'message') && meta.touched && !!fieldError;

  return (
    <FormGroup label={label} labelFor={name} helperText={helperText} $isRequired={required}>
      <StyledInputGroup
        {...field}
        {...inputGroupProps}
        id={name}
        $isError={showErrorStyle}
        onBlur={e => {
          field.onBlur(e);
          inputGroupProps?.onBlur?.(e);
        }}
        onChange={e => {
          field.onChange(e);
          props?.onChange?.(e);
          if (maxDecimalPlaces && props.type === 'number') {
            helper.setValue(handleMaxDecimalPlaces(e.target.value, maxDecimalPlaces));
          }
        }}
      />
      {showErrorMessage && <FieldError name={name} />}
    </FormGroup>
  );
};
